/**
 * Pure helpers: listing category → property type UI options (testable without React).
 */

/**
 * When the backend sends no selectable categories (`listing_categories: []`) but the listing's
 * category is a sub-type under a grouped parent that itself has sub-types — Land (Residential/
 * Commercial/Industrial Land, Agriculture Plot) or Building (Residential/Commercial Building) —
 * surface those sibling sub-types from the full category tree so Sell/Rent listings can still
 * see and change their property type. Returns [] for plain leaf types whose parent is the
 * purpose itself (e.g. Apartment, Villa), which have no sub-types to choose.
 *
 * @param {object} params
 * @param {Array<object>|null|undefined} params.categories full category tree
 * @param {object|null|undefined} params.listingCategory the edited listing's `listing_category`
 * @param {number|string|null|undefined} params.activePurposeCategoryId resolved purpose id
 * @returns {Array<object>} sibling sub-type category rows (unmapped)
 */
export function deriveGroupedSubTypeOptions({ categories, listingCategory, activePurposeCategoryId }) {
  if (!Array.isArray(categories) || categories.length === 0 || !listingCategory) return [];
  const groupParentId = listingCategory.parent_id;
  if (groupParentId == null) return [];
  // Leaf property type: its parent is the purpose itself, so there is no grouping to expand.
  if (activePurposeCategoryId != null && String(groupParentId) === String(activePurposeCategoryId)) {
    return [];
  }
  const groupParent = categories.find((c) => String(c.id) === String(groupParentId));
  if (!groupParent) return [];
  // The grouping node (e.g. Land / Building) must sit directly under the active purpose.
  if (
    activePurposeCategoryId != null &&
    String(groupParent.parent_id) !== String(activePurposeCategoryId)
  ) {
    return [];
  }
  return categories.filter((c) => String(c.parent_id) === String(groupParentId));
}

/**
 * @param {object} params
 * @param {Array<object>|null|undefined} params.categories
 * @param {number|string|null|undefined} params.activePurposeCategoryId
 * @param {boolean} params.shouldPreferPayloadCategoriesForPropertyType
 * @param {Array<object>|null|undefined} params.payloadListingCategories
 * @param {boolean} params.isDailyRental
 * @param {boolean} params.isListingLinkedToProject
 * @param {number|string|null|undefined} params.formikPropertyType
 * @param {Array<number|string>|null|undefined} params.projectPropertyTypeExternalIdOrder
 * @param {{ getLocalisedString: (row: object, key: string) => string }} params.tenantUtils
 * @param {(name: string) => unknown} params.getPropertyTypeIcon
 * @param {boolean} [params.lockGroupedSubTypesOnEdit] - When true, never derive Land/Building
 *   sibling sub-type options from empty payload categories — Property Type stays fully locked
 *   on edit for grouped types instead of offering sibling sub-types to switch between.
 */
export function buildPropertyTypeOptions({
  categories,
  activePurposeCategoryId,
  shouldPreferPayloadCategoriesForPropertyType,
  payloadListingCategories,
  isDailyRental,
  isListingLinkedToProject,
  formikPropertyType,
  projectPropertyTypeExternalIdOrder,
  listingCategory,
  tenantUtils,
  getPropertyTypeIcon,
  lockGroupedSubTypesOnEdit = false,
}) {
  const mapCategoryRow = (cat) => {
    const propertyTypeName = tenantUtils.getLocalisedString(cat, 'name') || cat.name;
    // Icon lookup keys off the English name; getLocalisedString would return the Arabic
    // string in /ar, which matches no case and gives every type the same fallback icon.
    const iconLookupName = typeof cat?.name === 'string' ? cat.name : cat?.name?.en || propertyTypeName;
    const icon = getPropertyTypeIcon(iconLookupName);
    return {
      key: cat.id,
      label: propertyTypeName,
      value: cat.id,
      id: cat.id,
      name: propertyTypeName,
      ...(icon && { icon }),
    };
  };

  if (isDailyRental && Array.isArray(categories) && categories.length > 0 && activePurposeCategoryId != null) {
    const isResortCategory = (cat) => {
      const slug = String(cat?.slug || '').toLowerCase().trim();
      return slug === 'resorts' || slug === 'resort';
    };
    const propertyTypes = categories.filter(
      (cat) =>
        cat != null &&
        cat.parent_id != null &&
        String(cat?.purpose_hash?.slug || '').toLowerCase().trim() === 'daily-rental' &&
        !isResortCategory(cat),
    );
    if (propertyTypes.length > 0) {
      propertyTypes.sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
      return propertyTypes.map(mapCategoryRow);
    }
  }

  if (
    isListingLinkedToProject &&
    !isDailyRental &&
    Array.isArray(categories) &&
    categories.length > 0 &&
    activePurposeCategoryId != null
  ) {
    const allowedIds = new Set();
    if (shouldPreferPayloadCategoriesForPropertyType && payloadListingCategories?.length) {
      payloadListingCategories.forEach((c) => {
        if (c?.id != null) allowedIds.add(c.id);
      });
    }
    const cur = formikPropertyType;
    if (cur != null && cur !== '') allowedIds.add(cur);

    const isDescendantOfPurpose = (cat) => {
      if (!cat || cat.id === activePurposeCategoryId) return false;
      if (cat.parent_id === activePurposeCategoryId) return true;
      return Array.isArray(cat.breadcrumbs) && cat.breadcrumbs.some((b) => b?.id === activePurposeCategoryId);
    };
    const pool = categories.filter(isDescendantOfPurpose);

    let propertyTypes;
    if (projectPropertyTypeExternalIdOrder?.length) {
      const used = new Set();
      propertyTypes = [];
      for (const wantedExtId of projectPropertyTypeExternalIdOrder) {
        const target = String(wantedExtId);
        const found = pool.find((c) => !used.has(c.id) && c?.external_id != null && String(c.external_id) === target);
        if (found) {
          used.add(found.id);
          propertyTypes.push(found);
        }
      }
      const curId = formikPropertyType;
      if (curId != null && curId !== '') {
        const curCat = pool.find((c) => String(c.id) === String(curId));
        if (curCat && !used.has(curCat.id)) {
          propertyTypes.push(curCat);
          used.add(curCat.id);
        }
      }
    } else {
      propertyTypes = [...pool].sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
    }

    return propertyTypes.map((cat) => ({
      ...mapCategoryRow(cat),
      ...(allowedIds.size > 0 ? { disabled: !allowedIds.has(cat.id) } : {}),
    }));
  }

  if (shouldPreferPayloadCategoriesForPropertyType) {
    if (payloadListingCategories.length > 0) {
      const sorted = [...payloadListingCategories].sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
      return sorted.map(mapCategoryRow);
    }
    // Empty payload categories: fall back to the listing's grouped sub-types (Land / Building)
    // from the full tree so the property type stays visible and selectable on Sell/Rent edits —
    // unless the tenant wants Property Type fully locked on edit for grouped types instead.
    if (!lockGroupedSubTypesOnEdit) {
      const grouped = deriveGroupedSubTypeOptions({ categories, listingCategory, activePurposeCategoryId });
      if (grouped.length > 0) {
        return [...grouped]
          .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
          .map(mapCategoryRow);
      }
    }
    return [];
  }
  if (!categories || categories.length === 0 || !activePurposeCategoryId) return [];

  const propertyTypes = categories.filter((cat) => cat.parent_id === activePurposeCategoryId);
  propertyTypes.sort((a, b) => (a.display_order || 0) - (b.display_order || 0));

  return propertyTypes.map(mapCategoryRow);
}

/** When payload categories are empty but Formik still has a property type, expose one synthetic option. */
export function buildPropertyTypeSelectOptions({
  propertyTypeOptions,
  hasEmptyPayloadListingCategories,
  formikPropertyType,
  formikPropertyTypeLabel,
  listingCategory,
  tenantUtils,
}) {
  if (propertyTypeOptions.length > 0) return propertyTypeOptions;
  if (hasEmptyPayloadListingCategories && formikPropertyType != null && formikPropertyType !== '') {
    const id = formikPropertyType;
    const label =
      formikPropertyTypeLabel ||
      tenantUtils.getLocalisedString(listingCategory, 'name') ||
      listingCategory?.name ||
      String(id);
    return [{ id, name: label, label, value: id, key: id }];
  }
  return propertyTypeOptions;
}

export function computeMaskPropertyTypeSelectValue({
  shouldRenderPropertyTypeAsSelect,
  isPropertyTypeControlDisabled,
  formikPropertyType,
  propertyTypeOptions,
}) {
  if (!shouldRenderPropertyTypeAsSelect || isPropertyTypeControlDisabled) return false;
  if (formikPropertyType == null || formikPropertyType === '') return false;
  if (!propertyTypeOptions.length) return false;
  return !propertyTypeOptions.some((pt) => String(pt?.id) === String(formikPropertyType));
}
